import camelCase from "lodash/camelCase";

import { TemplateTypeEnum } from "@/models/workPaperTemplate";

// Ids are stripped from URLs so pages group together when reporting on
// pageviews, matching `rewriteUrlForMetrics` in the platform client. Midship's
// ids are UUIDs where AuditBoard's are integers, so the pattern differs but the
// job is the same.
const ID_SEGMENT =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Analytics values use product vocabulary, not code identifiers (glossary:
// "test session", never "control session"). Also pins values across route
// param renames so chart history doesn't split.
const PARAM_VOCABULARY: Record<string, string> = {
  controlSession: "testSession",
};

// Routes whose first segment belongs to the pre-login / account surface
// rather than a product area.
const AUTH_SEGMENTS = new Set([
  "signin",
  "referral",
  "invite",
  "reset-password",
  "sso",
  "auth",
  "accept-terms",
  "terms",
  "subscription-inactive",
]);

// Routes that never fire a pageview, the counterpart to the platform client's
// `DO_NOT_TRACK_PAGES`. The OAuth callbacks carry `code` and `state` in the
// query — an authorization code and a CSRF token, neither of which is a record
// id — and a redirect waypoint is not a page anyone visits. Unlike the platform
// we install metrics before auth, so these routes are in scope at all.
const UNTRACKED_SEGMENTS = new Set(["sso", "auth"]);

// Routes that only ever redirect, matched exactly rather than by segment —
// `/audit` is a waypoint but `/audit/documents` is a real page. `/` renders
// `<Navigate to="/audit">` and `/audit`'s index renders
// `<Navigate to="/audit/controls/v2">`, so without this a cold start reports
// three visits for one, and `/` has no segments at all so its `viewLocation`
// would be an empty string.
const UNTRACKED_PATHS = new Set(["/", "/audit"]);

// Redirect waypoints whose path carries a secret. The Optro launch page shows a
// spinner while it resolves a single-use launch token, then navigates away; the
// token is not a UUID, so URL masking would not catch it.
const UNTRACKED_PREFIXES = ["/audit/optro/launch/"];

/**
 * Whether this route is excluded from tracking entirely. Case-insensitive,
 * like React Router's matching, so `/Audit/Optro/Launch/<token>` is excluded
 * along with every other spelling that renders the same page.
 */
export function isUntrackedRoute(pathname: string): boolean {
  const lowered = pathname.toLowerCase();
  const normalised = lowered.length > 1 ? lowered.replace(/\/$/, "") : lowered;
  if (UNTRACKED_PATHS.has(normalised)) return true;
  if (UNTRACKED_PREFIXES.some((prefix) => lowered.startsWith(prefix)))
    return true;
  const first = lowered.split("/").find(Boolean);
  return first !== undefined && UNTRACKED_SEGMENTS.has(first);
}

// Top-level segments that hold many feature areas rather than being one.
const CONTAINER_SEGMENTS = new Set(["audit", "dashboard"]);

// Multi-segment areas inside a container, longest-lived name first. Keyed on
// the path beneath the container.
const CONTAINER_AREA_VOCABULARY: Record<string, string> = {
  "control-sessions/session": "testSession",
  "work-step-sessions/session": "workStepTestSession",
  optro: "optroLaunch",
};

// Every value `module` is allowed to take. Charts group by this property, and
// Amplitude cannot clean up property values after the fact, so an unrecognised
// route must collapse to `other` rather than mint a new value — otherwise one
// stale link or crawler hit permanently adds a dimension entry. Adding a route
// means adding its area here; forgetting means events land in `other`, which
// is the failure mode we want.
const MODULE_AREAS = new Set([
  "auth",
  "other",
  // top level
  "admin",
  "support",
  "dev",
  // under /audit
  "audit",
  "createControl",
  "controls",
  "workPaperTemplates",
  "documents",
  "libraries",
  "approvals",
  "auditLogs",
  "reportExtraction",
  "testSession",
  "workStepTestSession",
  "fieldwork",
  "workSteps",
  "optroLaunch",
  // under /dashboard
  "dashboard",
  "apiKeys",
  "extraction",
  "templates",
]);

// Query params kept on `currentURL`, each with every value the app defines for
// it. Anyone can type anything into the address bar, so a value is kept only
// if it is one of these; everything else is dropped — the app also puts free
// text (`search`), emails and opaque codes in the query, and none of those may
// reach Amplitude. Only the first occurrence counts, as `searchParams.get` does.
// `sortBy` is left out: its values are table column keys spread across pages.
// A new param or value stays out until it is added here, the same fail-safe
// as `MODULE_AREAS`.
const TRACKED_QUERY_VALUES: Record<string, ReadonlySet<string>> = {
  mode: new Set(["refine"]),
  tab: new Set(["controls", "fieldwork"]),
  type: new Set<string>(TemplateTypeEnum.options),
  sortOrder: new Set(["asc", "desc"]),
};

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The `currentURL` property: route param values become `:<paramName>`, any
 * remaining UUID becomes `:id`, and only allowlisted query params are kept, so
 * pageviews on the same page group together and nothing user-supplied leaks.
 */
export function rewriteUrlForMetrics(
  pathname: string,
  search: string,
  params: Record<string, string | undefined>,
): string {
  const paramNameByValue = new Map<string, string>();
  for (const [name, value] of Object.entries(params)) {
    if (value) paramNameByValue.set(value, name);
  }

  const path = pathname
    .split("/")
    .map((segment) => {
      const paramName = paramNameByValue.get(decodeSegment(segment));
      return paramName ? `:${paramName}` : segment;
    })
    .join("/")
    .replace(ID_SEGMENT, ":id");

  const query = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const [key, allowed] of Object.entries(TRACKED_QUERY_VALUES)) {
    const value = query.get(key);
    if (value !== null && allowed.has(value)) kept.set(key, value);
  }
  const keptQuery = kept.toString();

  return keptQuery ? `${path}?${keptQuery}` : path;
}

/**
 * Dot-notated matched-route pattern for the `viewLocation` property:
 * `/audit/<uuid>/workpaper-v2` with params `{controlSessionId}` →
 * `audit.testSession.workpaperV2`. Param segments carry the param's name
 * (minus a trailing `Id`) run through the vocabulary map, so the value is
 * stable across id renames and directly groupable.
 */
export function deriveViewLocation(
  pathname: string,
  params: Record<string, string | undefined>,
): string {
  const paramNameByValue = new Map<string, string>();
  for (const [name, value] of Object.entries(params)) {
    if (value) paramNameByValue.set(value, name);
  }

  return pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      const paramName = paramNameByValue.get(segment);
      if (!paramName) return camelCase(segment);
      const stripped =
        paramName.length > 2 && paramName.endsWith("Id")
          ? paramName.slice(0, -2)
          : paramName;
      return PARAM_VOCABULARY[stripped] ?? stripped;
    })
    .join(".");
}

/**
 * Feature area for the `module` property — the house counterpart to the
 * constant `product: "Midship"`, answering "which part of the app?" (`controls`,
 * `workPaperTemplates`, `testSession`, `admin`).
 *
 * `/audit` and `/dashboard` are containers holding many areas rather than areas
 * themselves, so the area is the segment beneath them; taking the first segment
 * would make almost every event read `module: audit`. `/admin` stays whole, as
 * it does in the house reference, where users/roles/settings all fold into
 * `admin`.
 *
 * The result is constrained to `MODULE_AREAS`; anything else becomes `other`.
 */
export function deriveModule(pathname: string): string {
  const area = deriveArea(pathname);
  return MODULE_AREAS.has(area) ? area : "other";
}

function deriveArea(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const [first, ...rest] = segments;
  if (!first) return "audit";
  if (AUTH_SEGMENTS.has(first)) return "auth";
  if (!CONTAINER_SEGMENTS.has(first)) return camelCase(first);

  const nested = rest.join("/");
  for (const [prefix, name] of Object.entries(CONTAINER_AREA_VOCABULARY)) {
    if (nested === prefix || nested.startsWith(`${prefix}/`)) return name;
  }
  return rest[0] ? camelCase(rest[0]) : camelCase(first);
}
